import { SimpleGrid, Title, Flex, Card, Button, Stack, Textarea, Text } from "@mantine/core";
import { IconUser, IconCalendar, IconId } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { displayPatientName, formatDateOnly } from "../../../../utils/CommonUtil";
import DistancePrescriptionForm from "../patient-selector/DistancePrescriptionForm";
import SimpleTextEntyWithIcon from "./SimpleTextEntyWithIcon";
import { useTranslation } from "react-i18next";
import { updatePatient } from "../../../../utils/api-communication/RestApiCli";

export default function PatientInfoCard(props: {
  value?: any;
  withButton?: boolean;
  handleSelect?: (p: any) => void;
  hidePrescription?: boolean;
}) {
  const [patient, setPatient] = useState(null);
  const [commentDraft, setCommentDraft] = useState("");
  const [commentEditMode, setCommentEditMode] = useState(false);
  const [commentSaving, setCommentSaving] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    setPatient(props.value);
    setCommentDraft(props.value?.comment || "");
    setCommentEditMode(false);
  }, [props.value]);

  function updatePatientPrescription(values) {
    const updatedPatient = { ...patient };
    updatedPatient.distancePrescription = values;
    updatePatient(
      patient.uuid,
      updatedPatient,
      (res) => {
        const pat = res.data;
        patient.distancePrescription = pat.distancePrescription;
      },
      () => {},
      () => {},
      () => {},
      true,
    );
  }

  function savePatientComment() {
    const comment = commentDraft.trim();
    const updatedPatient = { ...patient, comment };
    setCommentSaving(true);
    updatePatient(
      patient.uuid,
      updatedPatient,
      (res) => {
        const savedComment = res.data?.comment || "";
        setPatient((prev) => (prev ? { ...prev, comment: savedComment } : prev));
        setCommentDraft(savedComment);
        setCommentEditMode(false);
        setCommentSaving(false);
      },
      () => setCommentSaving(false),
      () => setCommentSaving(false),
      () => setCommentSaving(false),
      true,
    );
  }

  function cancelCommentEdit() {
    setCommentDraft(patient?.comment || "");
    setCommentEditMode(false);
  }

  return (
    patient && (
      <Card mt={"md"} withBorder shadow="lg">
        <SimpleGrid
          cols={props.hidePrescription ? { base: 1 } : { base: 1, sm: 2 }}
          spacing={{ base: "sm", sm: "md" }}
        >
          <Stack>
            <Title size={"h4"}>{t("PatientList.patientInfo")}</Title>
            <SimpleTextEntyWithIcon
              textprops={{ w: 300 }}
              icon={<IconUser />}
              text={t("NewTest.PatientName") + ": " + displayPatientName(patient.name)}
            />
            <SimpleTextEntyWithIcon
              textprops={{ w: 300 }}
              icon={<IconCalendar />}
              text={t("NewTest.Dob") + ": " + formatDateOnly(patient.dob)}
            />
            <SimpleTextEntyWithIcon
              textprops={{ w: 300 }}
              icon={<IconId />}
              text={"ID: " + patient.uuid}
            />
          </Stack>
          {!props.hidePrescription && (
            <Card shadow="none">
              <DistancePrescriptionForm
                defaultValues={{
                  distancePrescription: patient.distancePrescription || {
                    leftEye: {
                      sphere: 0,
                      cylinder: 0,
                    },
                    rightEye: {
                      sphere: 0,
                      cylinder: 0,
                    },
                  },
                }}
                saveValues={(values) => updatePatientPrescription(values)}
              />
            </Card>
          )}
        </SimpleGrid>
        <Flex mt={"sm"} mb={"xs"} justify={"space-between"} align={"center"}>
          <Text color="dimmed">{t("NewTest.PatientNotes")}</Text>
          {commentEditMode && (
            <Button.Group>
              <Button
                onClick={cancelCommentEdit}
                variant="subtle"
                size="xs"
                color="red"
                disabled={commentSaving}
              >
                {t("OrganizationSettings.cancel")}
              </Button>
              <Button
                variant="subtle"
                size="xs"
                onClick={savePatientComment}
                loading={commentSaving}
                data-testid="patient-comment-save"
              >
                {t("OrganizationSettings.submit")}
              </Button>
            </Button.Group>
          )}
        </Flex>
        <Textarea
          data-testid="patient-comment-input"
          placeholder={commentEditMode ? t("NewTest.PatientNotesPlaceholder") : ""}
          autosize
          minRows={2}
          maxRows={4}
          value={commentDraft}
          onFocus={() => setCommentEditMode(true)}
          onChange={(event) => {
            setCommentDraft(event.currentTarget.value);
            setCommentEditMode(true);
          }}
        />
        {props.withButton && (
          <Flex mt={"sm"} justify={"center"}>
            <Button w={"30%"} mx={"lg"} onClick={() => props.handleSelect(patient)}>
              {t("SelectPatient")}
            </Button>
          </Flex>
        )}
      </Card>
    )
  );
}
